import { DashboardFEFilterTag, DashboardFEFilterTagList } from './types/dashboard.types';

import _ from 'lodash';
const resultIndicatorUtils = require('./utils/resultIndicatorUtils');
const { isAnalyticDataSource } = require('./analyticsConfig');
const { CREATIVE_INSIGHT_FILED_BY_BREAKDOWN } = require('./constants');

function getQueryRowToTableRow({ row, dimension, level, resultIndicator }) {
  if (!row) {
    throw new Error('row argument is missing');
  }
  if (!dimension) {
    console.warn('input dimension is missing');
  }
  const hasCell = !_.isEmpty(row.cells);
  if (hasCell) {
    resultIndicatorUtils.setResultMetrics(row, resultIndicator);
    if (dimension?.id === 'ad' && row.cells) {
      row.cells.adName = row.name;
    }
  }
  let children = undefined;
  if (row.children?.length) {
    children = [];
    for (const childRow of row.children) {
      children.push(getQueryRowToTableRow({ row: childRow, dimension, level, resultIndicator }));
    }
  }
  return {
    ..._.omit(row, ['cells', 'children']),
    cell: row.cells,
    dimension,
    rowId: row.id,
    tableId: row.id,
    level,
    isCustom: !row.adrielCampaignId,
    children,
  };
}

function getQueryRowsToTableRows(table) {
  if (!table?.rows) {
    return [];
  }
  const level = table?.dimension?.id;
  const dimension = table?.dimension;
  let convertedRows = [];
  const resultIndicator = resultIndicatorUtils.getResultIndicator(table.rows);
  for (const row of table.rows) {
    const newRow = getQueryRowToTableRow({ row, dimension, level, resultIndicator });
    convertedRows.push(newRow);
  }
  if (convertedRows.length === 1 && convertedRows[0].id === 'all') {
    convertedRows[0].level = 'all';
    convertedRows[0].dimension = { id: 'all', name: 'all' };
  }
  return convertedRows;
}

function getTableDashboardFilterToDataSourceQueryWhereConditions(dashboardFilter) {
  return Object.entries(dashboardFilter)
    .flatMap(([field, filter]) => {
      // @ts-ignore
      filter = _.uniq(filter);
      // if (Array.isArray(filter) && filter.length == 0) {
      //   return [];
      // }
      if (Array.isArray(filter) && filter.some(f => f?.level)) {
        const levelFilters = _.groupBy(filter, 'level');
        //@ts-ignore
        return Object.values(levelFilters).flatMap(levelFilter => {
          const values = _.uniq(levelFilter.map(f => f.value));
          let operator = ['title', 'titles'].includes(field) ? 'LIKE' : 'IN';
          if (['ga', 'ga4'].includes(field)) {
            operator = 'LIKE';
            return levelFilter.map(f => ({
              field: f.level,
              operator,
              values: [f.value],
            }));
          }
          if (field === 'ids') field = 'channelId';
          const cond = { field: field, level: levelFilter[0].level, operator, values };
          return cond;
        });
      } else if (field == 'metrics') {
        // @ts-ignore
        return filter.map(metricFilter => {
          let operator = null;
          if (metricFilter.operator == 'LESS_THAN') {
            operator = 'LESS_THAN';
          } else if (metricFilter.operator == 'GREATER_THAN') {
            operator = 'GREATER_THAN';
          } else if (metricFilter.operator == 'EQUAL') {
            operator = 'EQUAL';
          } else {
            throw new Error(`Unknown  metric filter operator '${metricFilter.operator}'`);
          }
          return {
            field: metricFilter.field,
            table: 'report',
            operator,
            value: metricFilter.value,
          };
        });
      } else if (['adAccountIds', 'campaignIds', 'adSetIds', 'adIds'].includes(field)) {
        const cond = { field: 'channelId', level: field.replace('Ids', ''), operator: 'IN', values: filter };
        return cond;
      } else {
        const cond = { field: field, operator: 'IN', values: Array.isArray(filter) ? filter : [filter] };
        return cond;
      }
    })
    .flat();
}

function isOtherAnalyticAdAccount(adAccount) {
  return (
    adAccount.channel === 'adverity' && adAccount?.adverityExtra?.adverityChannelName === 'Adjust'
    // || (adAccount.channel === 'csvWithDate' && adAccount.dataSourceName === 'Singular')
  );
}

function getDataSourceType(adAccount) {
  if (adAccount.isAdAccount) {
    return 'marketing';
  } else if (adAccount.is_analytic || isOtherAnalyticAdAccount(adAccount)) {
    return 'analytic';
  } else if (adAccount.isCustomReportAccount) {
    return 'normal';
  }
  return null;
}

function getAdAccountToDataSource(adAccount) {
  let dataSourceType = getDataSourceType(adAccount);
  const architectureSyncedAt =
    dataSourceType === 'marketing' ? adAccount.architectureSyncedAt : new Date().toISOString();
  const dataSource = {
    type: adAccount.channel,
    asset: {
      id: adAccount.channelId || adAccount.adAccountId,
      name: adAccount.name,
      architecture_synced_at: architectureSyncedAt,
    },
    isInternalDataSource: adAccount.external == false,
    settings: adAccount.settings,
    adverityExtra: {
      adverityChannelName: adAccount.adverityChannelName ?? adAccount.displayChannelName,
    },

    // old format
    connectorName: adAccount.channel,
    dataSourceId: adAccount.channelId || adAccount.adAccountId,
    dataSourceType,
    dataSourceName: adAccount.dataSourceName ?? adAccount.name,
    architectureSyncedAt,
  };
  if (isAnalyticDataSource(dataSource)) {
    dataSource.dataSourceType = 'analytic';
  }
  return dataSource;
}

function getDataSourceToAdAccount(dataSource) {
  return {
    channel: dataSource.connectorName,
    channelId: dataSource.dataSourceId,
    adAccountId: dataSource.dataSourceId,
    architectureSyncedAt: dataSource.architectureSyncedAt,
    name: dataSource.dataSourceName,
    external: !dataSource.isInternalDataSource,
    settings: dataSource.settings,
  };
}

function getTableArgumentsToDataSourceQuery({
  workspaceId,
  currency,
  dimensionId,
  originalDimensionId = null,
  childrenDimensionId = null,
  dashboardFilter,
  dateFilter,
  metricFilter = null,
  adAccount = null,
  adAccounts = null,
  sorting = undefined,
  reports = null,
  metrics,
  analyticAccount = null,
  isCustomTable = false,
  useCache = false,
  limit = null,
  widget = null,
}) {
  let table = 'architectureAndReport';
  if (widget?.mmo === 'budgetHistory') {
    table = 'architectureAndBudget';
  }
  // if (!adAccount.is_analytics) {
  //   table = 'architectureAndReport'
  // }

  if (isCustomTable === false) {
    // In marketing table, "subChannels" is always required because of facebook. requested by BK
    metrics.push('subChannels');
  }

  if (metrics.includes('results')) {
    metrics.push('resultIndicators');
  }

  if (metrics.includes('frequency')) {
    metrics.push('impressions', 'reach', 'impressionsForFrequency', 'reachForFrequency');
  }

  if (CREATIVE_INSIGHT_FILED_BY_BREAKDOWN[dimensionId]) {
    metrics.push(CREATIVE_INSIGHT_FILED_BY_BREAKDOWN[dimensionId]);
  }
  if (metrics.includes('amountSpentPercentage')) {
    metrics.push('budget', 'lifeTimeSpend', 'lifeTimeBudgetAmount');
  }
  if (metrics.includes('budget')) {
    metrics.push('lifeTimeBudgetAmount', 'dailyBudgetAmount');
  }
  if (metrics.includes('pixelRoas')) {
    metrics.push('pixelRevenue');
  }
  if (!isCustomTable) {
    metrics.push('clicks');
    metrics.push('spend');
    metrics.push('impressions');
  }
  metrics = _.uniq(metrics);

  const groupBy = (childrenDimensionId || dateFilter.dateGroup) ?? 'all';
  const where = getTableDashboardFilterToDataSourceQueryWhereConditions(dashboardFilter);
  //   if (!limit) {
  if (groupBy !== 'all') {
    limit = 20;
  } else {
    limit = ['campaign', 'adSet', 'ad', 'keyword'].includes(dimensionId) ? 200 : null;
  }
  //   }
  const query = {
    workspaceId,
    currency,
    breakdowns: [dimensionId],
    fields: metrics,
    table,
    useCache,
    where,
    when: { from: dateFilter.from, to: dateFilter.to, groupBy },
    limit,
  };
  return query;
}

function queryReRenderCallBack({ dataSource, table, refreshTime, isCacheLoaded = false }) {
  // TODO convert dataSource to adAccount
  if (this.reRenderCallBack) {
    const adAccount = dataSource;
    const convertedRows = getQueryRowsToTableRows(table);
    this.reRenderCallBack({
      table: convertedRows,
      lastUpdatedAt: refreshTime,
      adAccount,
      refreshTime,
      isCacheLoaded,
      headers: table.headers,
      warnings: table.warnings,
    });
  }
}
function queryFailCallBack({ dataSource, error }) {
  if (this.failCallBack) {
    let adAccount;
    if (dataSource) {
      adAccount = getDataSourceToAdAccount(dataSource);
    }
    return this.failCallBack({ adAccount, error });
  }
}
function queryDoneCallBack({ table, refreshTime, isCacheLoaded = false, debug = false, jobsInfos }) {
  if (this.doneCallBack) {
    const convertedRows = getQueryRowsToTableRows(table);
    if (!refreshTime) {
      refreshTime = table.refreshTime;
    }
    if (!refreshTime) {
      refreshTime = table.refreshTime;
    }
    const doneCallBackOutput = {
      table: convertedRows,
      lastUpdatedAt: refreshTime,
      refreshTime,
      isCacheLoaded,
      headers: table.headers,
      warnings: table.warnings,
      jobsInfos,
    };
    if (debug) {
      console.log('queryDoneCallBack', _.cloneDeep(doneCallBackOutput));
    }
    return this.doneCallBack(doneCallBackOutput);
  }
}

module.exports = {
  getTableArgumentsToDataSourceQuery,
  getAdAccountToDataSource,
  getTableDashboardFilterToDataSourceQueryWhereConditions,
  getQueryRowsToTableRows,
  getQueryRowToTableRow,
  queryReRenderCallBack,
  queryFailCallBack,
  queryDoneCallBack,
  getDataSourceToAdAccount,
};
